import React from 'react';

import { useIntl } from 'react-intl';

import { TimePicker } from 'antd';
import moment, { type Moment } from 'moment';

import { DAYS_OF_WEEK } from '@src/constants/commons';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyShiftDayConfig } from '@src/packages/workJourney/types';

import { ResponsiveTable } from '@src/components/Table';

import styles from '../../../styles.module.less';

interface CollationShiftRow {
  id: string;
  dayLabel: string;
  scheduleLabel: string;
  dayValue: string | number;
}

type MomentWithLegacy = Moment & { _i?: { _i?: string } };

/**
 * CollationShiftSelector
 * Render a table with the days and the shift schedule selected,
 * allowing to define the collation start time with a single selector.
 */
const CollationShiftSelector = () => {
  const { workJourneyForm, setWorkJourneyForm } = useWorkJourneyContext();
  const intl = useIntl();

  const shift = workJourneyForm?.workShift;
  const weekDays = shift?.recurrence?.weeks?.[0]?.days || {};

  const formatHour = (v: string | null | undefined) => {
    if (!v) return '—';
    try {
      const m = moment(v, ['HH:mm', moment.ISO_8601], true);
      if (m.isValid()) {
        if (m.format) return m.format('HH');
        return (m as MomentWithLegacy)._i?._i;
      }
      return String(v).split(':')[0];
    } catch (e) {
      return String(v).split(':')[0] || '—';
    }
  };

  const dataSource: CollationShiftRow[] = DAYS_OF_WEEK.filter(d =>
    Object.prototype.hasOwnProperty.call(weekDays, d.value),
  )
    .sort((a, b) => a.order - b.order)
    .map(d => {
      const dayCfg = weekDays[d.value] as WorkJourneyShiftDayConfig | undefined;
      const start = dayCfg?.startTime || null;
      const end = dayCfg?.endTime || null;

      const scheduleLabel = start && end ? `${formatHour(start)} a ${formatHour(end)}` : '—';

      return {
        id: `day-${d.value}`,
        dayLabel: d.label,
        scheduleLabel,
        dayValue: d.value,
      };
    });

  const getBreakStartTime = (day: string | number) => {
    const workShift = workJourneyForm.workShift;
    if (!workShift?.recurrence?.weeks?.[0]?.days) return null;
    const dayData = workShift.recurrence.weeks[0].days[day] as WorkJourneyShiftDayConfig | undefined;
    const rawBreakStartTime = dayData?.startBreakTime;
    return rawBreakStartTime ? moment(rawBreakStartTime, 'HH:mm') : null;
  };

  const isValidMomentTime = (time: Moment | null) => {
    return time && moment.isMoment(time) && time.isValid();
  };

  const updateBreakStartTime = (day: string | number, newTime: Moment | null) => {
    setWorkJourneyForm(prevForm => {
      const newForm = { ...prevForm };
      if (!newForm.workShift?.recurrence?.weeks) return newForm;

      newForm.workShift = { ...newForm.workShift };
      newForm.workShift.recurrence = { ...newForm.workShift.recurrence };
      newForm.workShift.recurrence.weeks = [...newForm.workShift.recurrence.weeks];

      newForm.workShift.recurrence.weeks.forEach((week, weekIndex) => {
        newForm.workShift!.recurrence!.weeks![weekIndex] = { ...week };
        newForm.workShift!.recurrence!.weeks![weekIndex].days = { ...week.days };
        const existingDay = (week.days?.[day] ?? {}) as WorkJourneyShiftDayConfig;
        newForm.workShift!.recurrence!.weeks![weekIndex].days![day] = {
          ...existingDay,
          startBreakTime: isValidMomentTime(newTime) ? newTime.format('HH:mm') : null,
        };
      });

      return newForm;
    });
  };

  const columns = [
    {
      key: 'day',
      title: intl.formatMessage({ id: 'workJourney.table.day' }),
      dataIndex: 'dayLabel',
      align: 'left' as const,
    },
    {
      key: 'shiftSchedule',
      title: intl.formatMessage({ id: 'workJourney.table.shiftSchedule' }),
      dataIndex: 'scheduleLabel',
      align: 'left' as const,
    },
    {
      key: 'breakStart',
      title: intl.formatMessage({ id: 'workJourney.table.breakStart' }),
      dataIndex: 'breakStart',
      align: 'left' as const,
      render: (record: CollationShiftRow) => {
        const day = record.dayValue;
        return (
          <TimePicker
            format="HH:mm"
            style={{ width: '100%' }}
            placeholder={intl.formatMessage({ id: 'workJourney.table.noBreak' })}
            value={getBreakStartTime(day)}
            onChange={e => {
              updateBreakStartTime(day, e);
            }}
          />
        );
      },
    },
  ];

  return (
    <div className={styles.collationShiftSelector}>
      {workJourneyForm.workShift ? (
        <ResponsiveTable dataSource={dataSource} columns={columns} />
      ) : (
        <></>
      )}
    </div>
  );
};

export default React.memo(CollationShiftSelector);
